import { type ReactNode } from "react";

import classNames from "classnames";

import { Spinner } from "../Spinner/Spinner";

import style from "./PricingCard.module.css"; // eslint-disable-line postcss-modules/no-unused-class

export type PricingCardFeatureItemIcon = "check" | "dot";

export interface PricingCardFeatureItem {
  text: string;
  icon?: PricingCardFeatureItemIcon;
  subItems?: string[];
}

export interface PricingCardFeatureSection {
  category: string;
  items: PricingCardFeatureItem[];
}

export interface PricingCardSeatType {
  label: string;
  color: "blue" | "orange";
}

export interface PricingCardProps {
  planName: string;
  planBadgeVariant?: "default" | "dark" | "blue";
  pricePrefix?: string;
  price?: ReactNode;
  pricePeriod?: string;
  priceSubline?: string;
  subtitle?: string;
  tagline?: string;
  ctaSlot: ReactNode;
  features: PricingCardFeatureSection[];
  seatTypes?: PricingCardSeatType[];
  highlighted?: boolean;
  className?: string;
  children?: ReactNode;
}

const CheckIcon = () => (
  <svg
    aria-hidden="true"
    className={style.featureIcon}
    fill="none"
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 8l2 2 4-4"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const DotIcon = () => (
  <svg
    aria-hidden="true"
    className={classNames(style.featureIcon, style.featureIconDot)}
    height="16"
    viewBox="0 0 16 16"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" fill="currentColor" r="5" />
  </svg>
);

export const PricingCard = ({
  planName,
  planBadgeVariant = "default",
  pricePrefix,
  price,
  pricePeriod = "/month",
  priceSubline,
  subtitle,
  tagline,
  ctaSlot,
  features,
  seatTypes,
  highlighted = false,
  className,
  children,
}: PricingCardProps) => {
  return (
    <div className={classNames(style.card, highlighted && style.cardHighlighted, className)}>
      <div className={style.header}>
        <span className={classNames(style.planBadge, style[planBadgeVariant])}>{planName}</span>

        <div className={style.pricingBlock}>
          <div className={style.pricingRow}>
            {pricePrefix && <span className={style.pricePrefix}>{pricePrefix}</span>}
            <span className={style.price}>{price ?? <Spinner size="sm" />}</span>
            <span className={style.pricePeriod}>{pricePeriod}</span>
          </div>
          {priceSubline && <p className={style.priceSubline}>{priceSubline}</p>}
          {subtitle && price !== undefined && <p className={style.subtitle}>{subtitle}</p>}
        </div>

        {ctaSlot}

        {tagline && <p className={style.tagline}>{tagline}</p>}
      </div>

      <div className={style.featureList}>
        {features.map((section) => (
          <div className={style.featureSection} key={section.category}>
            {section.category && <span className={style.categoryLabel}>{section.category}</span>}
            <ul className={style.items}>
              {section.items.map((item) => (
                <li className={style.item} key={item.text}>
                  {item.icon === "dot" ? <DotIcon /> : <CheckIcon />}
                  <div className={style.itemContent}>
                    <span>{item.text}</span>
                    {item.subItems && item.subItems.length > 0 && (
                      <ul className={style.subItems}>
                        {item.subItems.map((sub) => (
                          <li className={style.subItem} key={sub}>
                            {sub}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {children && <div className={style.cardBody}>{children}</div>}

      {seatTypes && seatTypes.length > 0 && (
        <div className={style.seatTypes}>
          <span className={style.categoryLabel}>Available seat types</span>
          <div className={style.seatTypeBadges}>
            {seatTypes.map((seat) => (
              <span className={classNames(style.seatBadge, style[seat.color])} key={seat.label}>
                {seat.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
